import {BehaviorSubject, from, Observable, of, throwError} from 'rxjs';
import {catchError, filter, finalize, mergeMap, switchMap, take} from 'rxjs/operators';
import {Injectable, Injector} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {LoginHelperService} from './login-helper.service';
import {hoslog} from '../../app.constants';
import {NoInternetConnectionService} from '../../services/no-internet-connection.service';
import * as Sentry from '@sentry/angular';

@Injectable()
export class HttpInterceptorService implements HttpInterceptor {

  private _loginHelperService: LoginHelperService = null;

  isRefreshingToken = false;
  // private _apiHelperService: ApiHelperService = null;


  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(private injector: Injector, private noInternetConnectionService: NoInternetConnectionService) {
  }

  get loginHelperService(): LoginHelperService {
    if (this._loginHelperService == null) {
      // I have to use the injector to avoid circular dependencies
      this._loginHelperService = this.injector.get(LoginHelperService);
    }
    return this._loginHelperService;
  }

  /*get apiHelperService(): ApiHelperService {
    if (this._apiHelperService == null) {
      // I have to use the injector to avoid circular dependencies
      this._apiHelperService = this.injector.get(ApiHelperService);
    }
    return this._apiHelperService;
  }*/

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const me = this;

    // The OAuth token endpoint authenticates via client_id/client_secret.
    // Never add a Bearer token to it and never treat its errors as auth failures —
    // doing so causes a deadlock where the refresh request waits for itself.
    const isTokenEndpoint = req.url.includes('/oauth/token');
    if (isTokenEndpoint) {
      return next.handle(req);
    }

    // Adding authorization token if required
    if (me.loginHelperService.isLoggedIn$.value) {
      // Proactive expiry check: refresh if the token is expired or within 60 s
      // of expiry to avoid clock-skew 401s from the server.
      if (me.loginHelperService.isTokenExpiringSoon()) {
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Access token expired — triggering proactive refresh',
          level: 'warning',
          data: {
            expiredAt: me.loginHelperService.access_token_expiration
              ? new Date(me.loginHelperService.access_token_expiration).toISOString()
              : null,
            url: req.url,
          },
        });
        return this.authError(req, next);
      }
      const sessionToken = me.loginHelperService.access_token;
      req = this.addToken(req, sessionToken);
    }

    return next.handle(req).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse) {
          if(error.status == 504) {
            hoslog('504 error');
            if(this.noInternetConnectionService.isInternetAvailable$.value) {
              this.noInternetConnectionService.changeIsInternetAvailable(false);
            }
            return throwError(error.error);
          } else if(me.loginHelperService.isLoggedIn$.value) {
            switch ((<HttpErrorResponse>error).status) {
              case 401:
                return this.authError(req, next);
              default:
                return throwError(error.error);
            }
          } else {
            return throwError(error.error);
          }
        } else {
          return throwError(error.error);
        }
      })
    );
  }

  authError(req: HttpRequest<any>, next: HttpHandler) {
    // console.log('Auth error - trying to refresh the token');

    const me = this;

    if (!this.isRefreshingToken) {
      // console.log('Get new token');
      this.isRefreshingToken = true;

      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      this.tokenSubject.next(null);

      return from(me.loginHelperService.refreshAccessToken()).pipe( // refreshAccessToken() returns a promise, can it be converted in Observable
        mergeMap(res => {
          const sessionToken = me.loginHelperService.access_token;
          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Token refresh succeeded',
            level: 'info',
            data: {
              newExpiry: me.loginHelperService.access_token_expiration
                ? new Date(me.loginHelperService.access_token_expiration).toISOString()
                : null,
            },
          });
          this.tokenSubject.next(sessionToken);
          // redo the call
          return next.handle(this.addToken(req, sessionToken));
        }),
        catchError(error => {
          // Refresh failed (e.g. refresh token expired) — force logout so the
          // UI shows the login form rather than leaving the user in a broken state.
          Sentry.captureException(error, {
            tags: {context: 'token_refresh_failed'},
            extra: {
              tokenExpiration: me.loginHelperService.access_token_expiration,
              hasRefreshToken: !!me.loginHelperService.refresh_token,
            },
          });
          me.loginHelperService.doLogout();
          return throwError(error);
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        })
      );
    } else {
      // console.log('Return token subject');
      return this.tokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          return next.handle(this.addToken(req, token));
        }));
    }
  }

  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    // updating the header for the next call
    // console.log('addToken: ' + token);
    return req.clone({ setHeaders: { Authorization: 'Bearer ' + token }});
  }
}
